import React, { useEffect, useState } from 'react';
import { Button, Confirm, Dimmer, Icon, Loader } from 'semantic-ui-react';
import { toast, SemanticToastContainer } from 'react-semantic-toasts';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { useAuth } from '../../providers/AuthenticationProvider';
import { useNavigate } from 'react-router';
import { useGenericPoints } from '../../providers/GenericPointProvider';
import { AddButton } from '../../components/AddButton/AddButton';
import { GenericPointModal } from './GenericPointModal';
import './GenericPoints.css';

const WEB_PAGE = 'Generic Points';

const POINT_TYPES = [
    { code: 'EP', label: 'Energy Points' },
    { code: 'VP', label: 'Variable Points' },
    { code: 'PP', label: 'Position Points' },
    { code: 'BP', label: 'Binary Points' },
    { code: 'NP', label: 'Numeric Points' },
    { code: 'GP', label: 'Global Points' },
];

export const GenericPoints = () => {
    const { checkAuth, getUserType } = useAuth();
    const navigate = useNavigate();

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    const {
        genericPoints, total, pointType, pageIndex, pageSize, sortState,
        loading, setPointType, setPageIndex, setSortState, getGenericPoints, deleteGenericPoint,
    } = useGenericPoints();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingPoint, setEditingPoint] = useState(null);
    const [deletingPoint, setDeletingPoint] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        getGenericPoints();
    }, [getGenericPoints]);

    const handleTypeChange = (code) => {
        setPointType(code);
        setPageIndex(0);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteGenericPoint(deletingPoint.id);
            setDeletingPoint(null);
            setDeleteError('');
            getGenericPoints();
            toast({
                type: 'success',
                title: 'Success',
                description: 'Generic point deleted successfully',
                animation: 'fade down',
                time: 4000,
            });
        } catch (err) {
            if (err?.response?.status === 409) {
                setDeleteError('This point cannot be deleted because it is in use.');
            } else {
                setDeleteError('An error occurred while deleting. Please try again.');
            }
        }
    };

    const handleAddClick = () => {
        setEditingPoint(null);
        setModalOpen(true);
    };

    const handleEditClick = (dataItem) => {
        setEditingPoint(dataItem);
        setModalOpen(true);
    };

    const TooltipCell = (props) => (
        <td title={props.dataItem[props.field]}>{props.dataItem[props.field]}</td>
    );

    const ActionCell = ({ dataItem }) => (
        <td>
            <Button size="small" onClick={() => handleEditClick(dataItem)}><Icon name="pencil" />Edit</Button>
            <Button size="small" onClick={() => { setDeleteError(''); setDeletingPoint(dataItem); }}><Icon name="trash" />Delete</Button>
        </td>
    );

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3rem' }}>
                <div style={{ flex: 1 }}>
                    <h1 style={{ margin: 0 }}>Generic Points</h1>
                </div>
                <Button.Group className="generic-points-type-filter" style={{ backgroundColor: '#f0f0f0', padding: '3px', borderRadius: '6px' }}>
                    {POINT_TYPES.map(t => (
                        <Button
                            key={t.code}
                            onClick={() => handleTypeChange(t.code)}
                            className={pointType === t.code ? 'active-type' : undefined}
                            style={pointType === t.code
                                ? { backgroundColor: 'white', color: '#444444', border: 'none', borderRadius: '4px', margin: '0 1px' }
                                : { backgroundColor: '#f0f0f0', color: '#777777', border: 'none' }
                            }
                        >
                            {t.label}
                        </Button>
                    ))}
                </Button.Group>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <AddButton onClick={handleAddClick}>Add Point</AddButton>
                </div>
            </div>

            <div style={{ position: 'relative' }}>
                <Dimmer active={loading} inverted>
                    <Loader />
                </Dimmer>
                <Grid
                    data={genericPoints}
                    total={total}
                    skip={pageIndex * pageSize}
                    take={pageSize}
                    pageable
                    sortable
                    sort={sortState.map(s => ({ field: s.fieldName, dir: s.dir }))}
                    onSortChange={e => setSortState(
                        e.sort.length ? [{ fieldName: e.sort[0].field, dir: e.sort[0].dir }] : []
                    )}
                    onPageChange={e => setPageIndex(e.page.skip / pageSize)}
                >
                    <GridColumn field="id" title="Num" width={100} />
                    <GridColumn field="code" title="Code" cell={TooltipCell} />
                    <GridColumn field="haystackTag" title="Haystack Tag" cell={TooltipCell} />
                    <GridColumn field="brick" title="Brick" width={90} cell={TooltipCell} />
                    <GridColumn field="description" title="Description" width={300} cell={TooltipCell} />
                    {pointType !== 'BP' && <GridColumn field="units" title="Units" width={100} cell={TooltipCell} />}
                    <GridColumn field="dataType" title="Data Type" width={120} />
                    {pointType === 'NP' && <GridColumn field="defaultValue" title="Default Value" width={130} cell={TooltipCell} />}
                    <GridColumn title="Actions" sortable={false} cell={ActionCell} width={200} />
                </Grid>
            </div>
            <GenericPointModal
                open={modalOpen}
                setOpen={setModalOpen}
                editingPoint={editingPoint}
                activePointType={pointType}
            />
            <Confirm
                open={deletingPoint !== null}
                onCancel={() => { setDeletingPoint(null); setDeleteError(''); }}
                onConfirm={handleDeleteConfirm}
                content={
                    deleteError
                        ? deleteError
                        : `Are you sure you want to delete point ${deletingPoint?.id}?`
                }
                confirmButton="Delete"
            />
            <SemanticToastContainer position="bottom-center" />
        </div>
    );
};
